import _find from 'lodash/find.js';

export const getBasename = () => {
  if (typeof window === 'undefined') return '';
  const path =
    window.routeInfo && window.routeInfo.basePath
      ? window.routeInfo.basePath
      : window.location.pathname;
  return path.split('/', 2).join('/');
};

export const getQueryParameters = () => {
  if (typeof window === 'undefined') {
    // Return a proxy that returns null for all properties during SSR
    return new Proxy({}, {
      get: () => null,
    });
  }
  return new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
}

export const getProtocolWithHost = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return '';
  const url = document.location;
  return `${url.protocol}//${url.host}`;
};

const isParentSelected = (options, choices, option) => {
  if (!option.isDependent) {
    return true;
  }

  const choice = choices[option.parentChoiceId];
  if (choice && choice.isSelected) {
    const option2 = options[choice.optionId];
    return option2 && isParentSelected(options, choices, option2);
  }

  return false;
};


export const isMultiSelectOption = (options, id) => options[id].tags.includes('multi-choice-group');

export const isMultiSelectChoice = (choices, id) => choices[id].tags.includes('multi-choice-values');

export const getMultiSelectPath = (props) => {
  const { id, value } = props;
  let colorNames = value;
  if (colorNames.match(/^\d/) && colorNames.indexOf('_') >= 0) {
    colorNames = value.substring(value.indexOf('_') + 1);
    // value.substring(value.indexOf('|') + 1);
  }

  if (colorNames.match(/^\d/) && colorNames.indexOf('|') >= 0) {
    colorNames = value.substring(value.indexOf('|') + 1);
    // value.substring(value.indexOf('|') + 1);
  }

  const queryKey = 'multiChoice=';
  const selectPath = `${queryKey}${id}_${colorNames.replace(/[\W_]+/g, ',')}`;

  return selectPath;
};

const getSelectedChoicesFromValues = (options, choices, optionId) => {
  const option = options[optionId];
  const { internalName } = option;

  const multiChoiceId = option.choices.find((id) => isMultiSelectChoice(choices, id)
  );
  const { value } = choices[multiChoiceId];
  if (value === 'ChoiceValues' || value === '') {
    return '';
  }
  const selectedChoice = getMultiSelectPath(choices[multiChoiceId]);

  return selectedChoice;
};

const getSelectedChoice = (options, choices, optionId) => {
  const option = options[optionId];
  return (
    option &&
    option.isActive &&
    isParentSelected(options, choices, option) &&
    option.choices.find((cid) => choices[cid].isSelected)
  );
};

export const getChoicePaths = (options, choices) => {
  const multiSelectOptionId = options.allIds.find((id) => isMultiSelectOption(options, id));

  const selectedChoices = options.allIds
    .map((id) => {
      if (id === multiSelectOptionId) {
        return getSelectedChoicesFromValues(
          options,
          choices,
          multiSelectOptionId
        );
      }
      return getSelectedChoice(options, choices, id);
    })
    .filter((choice) => choice);

  return `choices=${selectedChoices.join(',')}`;
};

const getFallbackPath = () => {
  if (typeof window === 'undefined') return ['', ''];
  return window.location.pathname.split(/(.*?\d{8})(?:\/|$)/);
};

export const getOtherQueryParameters = () => {
  if (typeof window === 'undefined') return '';
  // These are the query parameters that need to be manually handled elsewhere. Keep
  // them in lowercase.
  const queryParametersToIgnore = ['choices', 'logo', 'viewaccessories'];

  let additionalQueryStrings = '';
  let queryString = window.location.search;
  if (queryString.length > 0) {
    if (window.location.search[0] === '?') {
        queryString = queryString.substring(1);
    }

    const newQueryStringArray = [];
    const queryStringArray = queryString.split('&');
    queryStringArray.forEach(query => {
      const [key,] = query.split('=');
      if (!queryParametersToIgnore.includes(key.toLowerCase())) {
        newQueryStringArray.push(query);
      }
    });
      
    additionalQueryStrings = newQueryStringArray.join('&');

    if (additionalQueryStrings.length > 0) {
      additionalQueryStrings = `&${additionalQueryStrings}`;
    }
  }

  return additionalQueryStrings;
}

export const getPathFromSelectedChoices = (
  configurator,
  uploadImages,
  viewAccessories,
) => {
  let path = '';
  
  if (!path) {
    [, path] = getFallbackPath();
  }
  
  const { options, choices } = configurator;

  // Get selected choices
  const choicesQuery = getChoicePaths(options, choices);
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    const queryParams = [];

    // Add choices
    queryParams.push(choicesQuery);
    
    // Get and add viewAccessories
    const viewAccessoriesQuery = viewAccessories ? 'viewAccessories=true' : 'viewAccessories=false';
    queryParams.push(viewAccessoriesQuery);

    path = `${path}?${queryParams.join('&')}`;
  } else {
    // No query params, use default.
    path = `${path}?${choicesQuery}&viewAccessories=true`;
  }

  if (uploadImages.uploadImagesUrl) {
    // Add logo information
    path = `${path}&logo=${uploadImages.uploadImagesUrl}`;
  }
  
  return path;
};

export const getChoicePathsIfSelected = (options, choices, choiceId) => {
  const selectedChoices = options.allIds
    .map((oid) => {
      const optionChoices = options[oid].choices;
      if (optionChoices.includes(choiceId)) {
        return choiceId;
      }

      return optionChoices.find((cid) => choices[cid].isSelected);
    })
    .filter((item) => item);
  return `choices=${selectedChoices.join(',')}`;
};

export const getPreconfiguration = (options, choices, choicePath = null) => {
  const params = getQueryParameters();
  if (!params.choices) {
    return {};
  }

  const chocieIds = params.choices.split(',');
  if (!chocieIds || chocieIds.length === 0) {
    return {};
  }

  const configuration = {};
  chocieIds.forEach(choiceId => {
    if (!Number.isNaN(choiceId) && choiceId in choices) {
      configuration[choices[choiceId].optionId] = { choiceId };
    }
  })

  return configuration;
};